import ErrorBoundary from '@/components/base/ErrorBoundary';
import { useState, useCallback } from 'react';
import SplashScreen from './components/SplashScreen';
import LoginScreen from './components/LoginScreen';
import RegistroStep1 from './components/RegistroStep1';
import RegistroStep2 from './components/RegistroStep2';
import RegistroStep3 from './components/RegistroStep3';
import RegistroExito from './components/RegistroExito';
import ScreenInicio from './components/ScreenInicio';
import ScreenFamilia from './components/ScreenFamilia';
import ScreenPagos from './components/ScreenPagos';
import ScreenPlan from './components/ScreenPlan';
import BottomNav from '@/components/feature/BottomNav';
import { supabase, ADMIN_PHONE } from '@/lib/supabase';
import { authenticateWithBiometrics, hasStoredBiometric, registerBiometricCredential } from '@/lib/biometrics';

type AppScreen =
  | 'splash' | 'login' | 'registro-1' | 'registro-2'
  | 'registro-3' | 'registro-exito' | 'app' | 'pendiente-pago';

interface RegistroData {
  nombre: string; curp: string; telefono: string;
  email: string; password: string; confirmPassword: string;
  ciudad: string; estado: string; referidoPor: string;
  birthdateFromCurp: string; plan: 'familiar' | 'plus';
}

interface MemberUser {
  id: string; folio: string; nombre: string; apellido: string;
  nombreCompleto: string; curp: string; telefono: string;
  email: string; ciudad: string; estado_residencia: string;
  plan: string; planNombre: string; planPrecio: number;
  estado: string; fechaContratacion: string;
  fechaCarenciaFin: string; primer_pago_realizado: boolean;
}

const addDays = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
};

const getReferralIdFromUrl = () => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('ref')?.trim().toUpperCase() || '';
};

const getPlanName = (plan?: string) => (plan === 'familiar' ? 'Basico' : 'Prime');
const getPlanPrice = (plan?: string) => (plan === 'familiar' ? 289 : 399);
const isMissingSchemaError = (message?: string) =>
  Boolean(message && (message.includes('schema cache') || message.includes('column') || message.includes('relation')));

async function safeInsert(table: string, payload: Record<string, any> | Record<string, any>[]) {
  const { error } = await supabase.from(table).insert(payload);
  if (error) console.warn(`No se pudo guardar en ${table}:`, error.message);
  return !error;
}

export default function Home() {
  const initialReferrerId = getReferralIdFromUrl();
  const [screen, setScreen] = useState<AppScreen>(initialReferrerId ? 'registro-1' : 'splash');
  const [activeTab, setActiveTab] = useState('inicio');
  const [registroData, setRegistroData] = useState<Partial<RegistroData>>(
    initialReferrerId ? { referidoPor: initialReferrerId } : {}
  );
  const [currentUser, setCurrentUser] = useState<MemberUser | null>(null);
  const [openAddBeneficiarySignal, setOpenAddBeneficiarySignal] = useState(0);
  const [folio, setFolio] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [biometricSetupRequested, setBiometricSetupRequested] = useState(false);
  const [biometricsEnabled, setBiometricsEnabled] = useState(() => hasStoredBiometric());


  const fechaCobertura = addDays(90);

  const handleRegistrar = useCallback(() => setScreen('registro-1'), []);
  const handleLoginBack = useCallback(() => {
    setBiometricSetupRequested(false);
    setScreen('splash');
  }, []);
  const handleSplashEntrar = useCallback(() => setScreen('login'), []);
  const handleSetupBiometrics = useCallback(() => {
    setBiometricSetupRequested(true);
    setScreen('login');
  }, []);
  const handleAddBeneficiary = useCallback(() => {
    setActiveTab('familia');
    setOpenAddBeneficiarySignal((value) => value + 1);
  }, []);

  const loadMemberById = useCallback(async (memberId: string, fallbackUser?: any) => {
    if (memberId === 'admin' && fallbackUser) return fallbackUser;

    const { data, error: dbError } = await supabase
      .from('members')
      .select('*')
      .eq('id', memberId)
      .limit(1);

    if (dbError || !data?.[0]) return fallbackUser || null;

    const member = data[0];
    const partes = (member.nombre || '').split(' ');
    return {
      id: member.id,
      folio: member.folio,
      nombre: partes[0] || member.nombre,
      apellido: partes.slice(1).join(' '),
      nombreCompleto: member.nombre,
      curp: member.curp,
      telefono: member.telefono,
      email: member.email || '',
      ciudad: member.ciudad || '',
      estado_residencia: member.estado_residencia || '',
      plan: member.plan,
      planNombre: getPlanName(member.plan),
      planPrecio: getPlanPrice(member.plan),
      estado: member.estado,
      fechaContratacion: member.fecha_registro?.split('T')[0] || '',
      fechaCarenciaFin: member.fecha_carencia_fin || '',
      primer_pago_realizado: member.primer_pago_realizado,
    };
  }, []);

  const handleBiometricLogin = useCallback(async () => {
    try {
      const stored = await authenticateWithBiometrics();
      const user = await loadMemberById(stored.memberId, stored.user);
      if (!user) throw new Error('No se pudo recuperar tu membresia.');
      setCurrentUser(user);
      setActiveTab(!user.primer_pago_realizado && user.estado === 'pendiente_pago' ? 'pagos' : 'inicio');
      setScreen('app');
    } catch (e: any) {
      window.alert(e?.message || 'No se pudo entrar con biometria.');
    }
  }, [loadMemberById]);

  const finishLogin = useCallback(async (user: MemberUser) => {
    if (biometricSetupRequested) {
      try {
        await registerBiometricCredential({
          ...user,
          id: user.id,
          folio: user.folio,
          nombre: user.nombre,
          nombreCompleto: user.nombreCompleto,
          email: user.email,
          telefono: user.telefono,
        });
        setBiometricsEnabled(true);
        window.alert('Datos biometricos activados en este dispositivo.');
      } catch (e: any) {
        window.alert(e?.message || 'No se pudo activar la biometria en este dispositivo.');
      } finally {
        setBiometricSetupRequested(false);
      }
    }

    if (!user.primer_pago_realizado && user.estado === 'pendiente_pago') {
      setCurrentUser(user);
      setActiveTab('pagos');
      setScreen('app');
      return;
    }
    setCurrentUser(user);
    setScreen('app');
  }, [biometricSetupRequested]);

  // ── LOGIN: buscar por teléfono en Supabase ──────────────────────
  const handleLogin = useCallback(async (telefono: string) => {
    setLoading(true);
    setError('');

    // Administrador siempre entra
    const cleanPhone = telefono.replace(/\D/g, '');
    const isAdmin = cleanPhone.endsWith('6675680042');
    if (isAdmin) {
      setCurrentUser({
        id: 'admin', folio: 'CVR-ADMIN', nombre: 'Admin', apellido: 'Coverly',
        nombreCompleto: 'Admin Coverly', curp: 'ADMIN000000000000XX',
        telefono, email: 'admin@coverly.mx', ciudad: 'Culiacán',
        estado_residencia: 'Sinaloa', plan: 'plus', planNombre: 'Prime',
        planPrecio: 399, estado: 'activa', fechaContratacion: '2025-01-01',
        fechaCarenciaFin: '2025-04-01', primer_pago_realizado: true,
      });
      setScreen('app');
      setLoading(false);
      return;
    }

    // Buscar por teléfono — últimos 10 dígitos
    const last10 = cleanPhone.slice(-10);

    const { data: rows, error: dbErr } = await supabase
      .from('members')
      .select('*')
      .like('telefono', '%' + last10)
      .limit(1);

    if (dbErr) {
      setError('Error de conexión. Intenta de nuevo.');
      setLoading(false);
      return;
    }

    const data = rows && rows.length > 0 ? rows[0] : null;

    if (!data) {
      setError('No encontramos una cuenta con este número.');
      setLoading(false);
      return;
    }

    if (!data.primer_pago_realizado && data.estado === 'pendiente_pago') {
      const partesPendiente = (data.nombre || '').split(' ');
      setCurrentUser({
        id: data.id,
        folio: data.folio,
        nombre: partesPendiente[0] || data.nombre,
        apellido: partesPendiente.slice(1).join(' '),
        nombreCompleto: data.nombre,
        curp: data.curp,
        telefono: data.telefono,
        email: data.email || '',
        ciudad: data.ciudad || '',
        estado_residencia: data.estado_residencia || '',
        plan: data.plan,
        planNombre: getPlanName(data.plan),
        planPrecio: getPlanPrice(data.plan),
        estado: data.estado,
        fechaContratacion: data.fecha_registro?.split('T')[0] || '',
        fechaCarenciaFin: data.fecha_carencia_fin || '',
        primer_pago_realizado: data.primer_pago_realizado,
      });
      setActiveTab('pagos');
      setScreen('app');
      setLoading(false);
      return;
    }

    const partes = data.nombre.split(' ');
    setCurrentUser({
      id: data.id, folio: data.folio,
      nombre: partes[0] || data.nombre,
      apellido: partes.slice(1).join(' '),
      nombreCompleto: data.nombre,
      curp: data.curp, telefono: data.telefono,
      email: data.email || '', ciudad: data.ciudad || '',
      estado_residencia: data.estado_residencia || '',
      plan: data.plan, planNombre: getPlanName(data.plan),
      planPrecio: getPlanPrice(data.plan),
      estado: data.estado,
      fechaContratacion: data.fecha_registro?.split('T')[0] || '',
      fechaCarenciaFin: data.fecha_carencia_fin || '',
      primer_pago_realizado: data.primer_pago_realizado,
    });
    setScreen('app');
    setLoading(false);
  }, []);

  // ── REGISTRO: guardar en Supabase ───────────────────────────────
  const handleStep1 = useCallback((data: any) => {
    setRegistroData((prev) => ({ ...prev, ...data }));
    setScreen('registro-2');
  }, []);

  const handleStep2 = useCallback((plan: 'familiar' | 'plus') => {
    setRegistroData((prev) => ({ ...prev, plan }));
    setScreen('registro-3');
  }, []);

  const handleStep3 = useCallback(async (beneficiarios: any[], isTestCard: boolean = false) => {
    setLoading(true);
    setError('');
    console.log('handleStep3 called', {isTestCard, beneficiarios: beneficiarios.length, registroData});
    try {
    const plan = registroData.plan || 'familiar';
    const planPrecio = getPlanPrice(plan);
    const fechaCarenciaFin = new Date();
    fechaCarenciaFin.setDate(fechaCarenciaFin.getDate() + 90);
    const referidoPor = (registroData.referidoPor || '').trim().toUpperCase();

    const { data: authData, error: authErr } = registroData.email && registroData.password
      ? await supabase.auth.signUp({
          email: registroData.email,
          password: registroData.password,
          options: {
            data: {
              nombre: registroData.nombre,
              telefono: registroData.telefono,
              referido_por: referidoPor || null,
            },
          },
        })
      : { data: null, error: null };

    if (authErr) console.warn('Supabase Auth pendiente/no creado:', authErr.message);

    const { data: referrerRows } = referidoPor
      ? await supabase.from('members').select('id, folio').eq('folio', referidoPor).limit(1)
      : { data: null };
    const referrer = referrerRows && referrerRows.length > 0 ? referrerRows[0] : null;

    // Insertar member en Supabase
    const baseMemberPayload = {
      nombre: registroData.nombre,
      curp: registroData.curp?.toUpperCase(),
      telefono: registroData.telefono,
      email: registroData.email || null,
      plan,
      plan_precio: planPrecio,
      estado: 'pendiente_pago',
      canal_origen: 'app',
      fecha_carencia_fin: fechaCarenciaFin.toISOString().split('T')[0],
      primer_pago_realizado: false,
    };
    const memberPayload = {
      ...baseMemberPayload,
      auth_user_id: authData?.user?.id || null,
      password_configurado: Boolean(authData?.user?.id),
      referido_por_folio: referidoPor || null,
      referido_por_member_id: referrer?.id || null,
      programa_contigo_activo: true,
    };

    let memberResult = await supabase
      .from('members')
      .insert(memberPayload)
      .select()
      .single();

    if (memberResult.error && isMissingSchemaError(memberResult.error.message)) {
      memberResult = await supabase
        .from('members')
        .insert(baseMemberPayload)
        .select()
        .single();
    }

    const { data: member, error: memberErr } = memberResult;

    if (memberErr || !member) {
      const msg = memberErr?.message || 'Error desconocido';
      setError(`Error: ${msg}`);
      setLoading(false);
      return;
    }

    // Insertar beneficiarios
    if (beneficiarios.length > 0) {
      const bensToInsert = beneficiarios
        .filter(b => b.nombre.trim())
        .map(b => ({
          member_id: member.id,
          nombre: b.nombre,
          parentesco: b.parentesco,
          curp: b.curp?.toUpperCase() || null,
          fecha_nacimiento: b.fechaNacimiento || null,
          estado: 'carencia',
          fecha_carencia_fin: fechaCarenciaFin.toISOString().split('T')[0],
        }));
      if (bensToInsert.length > 0) {
        await supabase.from('beneficiarios').insert(bensToInsert);
      }
    }

    // Guardar aceptación legal
    await supabase.from('aceptaciones_legales').insert({
      member_id: member.id,
      version_contrato: '1.0',
      plan_aceptado: plan,
      fecha_aceptacion: new Date().toISOString(),
    });

    if (referidoPor) {
      await safeInsert('referidos', {
        referrer_member_id: referrer?.id || null,
        referred_member_id: member.id,
        referrer_folio: referidoPor,
        referred_folio: member.folio,
        status: 'pendiente_activacion',
        reward_status: 'pendiente',
        source: 'link_afiliado',
      });
    }

    await safeInsert('member_beneficios', {
      member_id: member.id,
      beneficio_code: 'descuentos_afiliados',
      status: 'elegible',
      source: 'registro_app',
      eligible_at: new Date().toISOString(),
      metadata: { plan },
    });

    // Insertar pago pendiente
    const periodoPago = new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    const basePagoPayload = {
      member_id: member.id,
      monto: planPrecio,
      metodo: 'pendiente',
      estado: 'pendiente',
      mes: periodoPago,
    };
    const pagoPayload = {
      ...basePagoPayload,
      folio: member.folio,
      metodo_pago: 'pendiente',
      periodo: periodoPago,
      procesador: 'pendiente_integracion',
      processor_status: 'pendiente_procesador',
    };
    const { error: pagoErr } = await supabase.from('pagos').insert(pagoPayload);
    if (pagoErr && isMissingSchemaError(pagoErr.message)) {
      await supabase.from('pagos').insert(basePagoPayload);
    }

    // Modo prueba: admin o tarjeta de prueba activan directo
    const phoneClean = (registroData.telefono || '').replace(/\D/g, '');
    const activarDirecto = phoneClean.endsWith('6675680042') || isTestCard;
    if (activarDirecto) {
      const fechaCarencia = new Date();
      fechaCarencia.setDate(fechaCarencia.getDate() + 90);
      await supabase.from('members').update({
        estado: 'carencia',
        primer_pago_realizado: true,
        tiene_tarjeta: isTestCard,
        fecha_activacion: new Date().toISOString().split('T')[0],
        fecha_carencia_fin: fechaCarencia.toISOString().split('T')[0],
        fecha_proximo_cobro: fechaCarencia.toISOString().split('T')[0],
      }).eq('id', member.id);
      // Marcar pago como pagado en modo prueba
      await supabase.from('pagos').update({
        estado: 'pagado',
        fecha_pago: new Date().toISOString().split('T')[0],
      }).eq('member_id', member.id);
    }

    const memberStatus = activarDirecto ? 'carencia' : 'pendiente_pago';
    const memberFirstPaid = activarDirecto;
    setCurrentUser({
      id: member.id,
      folio: member.folio,
      nombre: registroData.nombre?.split(' ')[0] || registroData.nombre || '',
      apellido: registroData.nombre?.split(' ').slice(1).join(' ') || '',
      nombreCompleto: registroData.nombre || '',
      curp: registroData.curp || '',
      telefono: registroData.telefono || '',
      email: registroData.email || '',
      ciudad: registroData.ciudad || '',
      estado_residencia: registroData.estado || '',
      plan,
      planNombre: getPlanName(plan),
      planPrecio,
      estado: memberStatus,
      fechaContratacion: new Date().toISOString().split('T')[0],
      fechaCarenciaFin: fechaCarenciaFin.toISOString().split('T')[0],
      primer_pago_realizado: memberFirstPaid,
    });
    if (!memberFirstPaid) setActiveTab('pagos');
    setFolio(member.folio);
    setLoading(false);
    setScreen('registro-exito');
    } catch(e: any) {
      setError('Error inesperado: ' + (e?.message || String(e)));
      setLoading(false);
    }
  }, [registroData]);


  const handleEntrar = useCallback(() => {
    if (currentUser) { setScreen('app'); return; }
    setScreen('splash');
  }, [currentUser]);

  // ── RENDER ──────────────────────────────────────────────────────
  if (screen === 'splash') return (
    <ErrorBoundary>
      <SplashScreen
        onRegistrar={handleRegistrar}
        onEntrar={handleSplashEntrar}
        onSetupBiometrics={handleSetupBiometrics}
        onBiometricLogin={handleBiometricLogin}
        biometricsEnabled={biometricsEnabled}
      />
    </ErrorBoundary>
  );

  if (screen === 'login') return (
    <ErrorBoundary>
      <LoginScreen
        onLogin={finishLogin}
        onBack={handleLoginBack}
        setupBiometrics={biometricSetupRequested}
      />
    </ErrorBoundary>
  );

  if (screen === 'registro-1') return (
    <RegistroStep1 onNext={handleStep1} onBack={() => setScreen('splash')} initialReferrerId={registroData.referidoPor || initialReferrerId} />
  );

  if (screen === 'registro-2') return (
    <RegistroStep2 onNext={handleStep2} onBack={() => setScreen('registro-1')} />
  );

  if (screen === 'registro-3') return (
    <RegistroStep3
      plan={registroData.plan || 'familiar'}
      nombre={registroData.nombre || ''}
      onNext={handleStep3}
      onBack={() => setScreen('registro-2')}
      loading={loading}
      errorMsg={error}
    />
  );

  if (screen === 'registro-exito') return (
    <RegistroExito
      nombre={registroData.nombre || ''}
      folio={folio}
      fechaCobertura={fechaCobertura}
      onEntrar={handleEntrar}
    />
  );

  if (screen === 'pendiente-pago') return (
    <div className="min-h-screen bg-[#0d0520] flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#534AB7' }}>
        <i className="ri-lock-line text-white text-3xl" />
      </div>
      <h1 className="text-white text-2xl font-bold mb-3">Pago pendiente</h1>
      <p className="text-gray-400 text-sm mb-8 leading-relaxed">
        Para acceder a tu membresía Coverly necesitas completar tu primer pago.
        Te contactaremos por WhatsApp con las instrucciones.
      </p>
      <a
        href="https://wa.me/526675680042?text=Hola%2C%20quiero%20activar%20mi%20membresía%20Coverly"
        className="w-full py-4 rounded-full text-white font-bold text-base flex items-center justify-center gap-2 mb-4"
        style={{ backgroundColor: '#25D366' }}
      >
        <i className="ri-whatsapp-line text-xl" /> Activar por WhatsApp
      </a>
      <button onClick={() => setScreen('splash')} className="text-gray-500 text-sm">
        Volver al inicio
      </button>
    </div>
  );

  // APP — Dashboard
  const userForScreen = currentUser || {
    id: '', folio: '', nombre: '', apellido: '', nombreCompleto: '',
    curp: '', telefono: '', email: '', ciudad: '', estado_residencia: '',
    plan: 'familiar', planNombre: 'Basico', planPrecio: 289,
    estado: 'activa', fechaContratacion: '', fechaCarenciaFin: '',
    primer_pago_realizado: true,
  };

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-screen overflow-hidden bg-[#F7F6FF]">
        <div className="flex-1 overflow-y-auto pb-20">
          {activeTab === 'inicio' && (
            <ScreenInicio
              user={userForScreen}
              onAddBeneficiary={handleAddBeneficiary}
              onGoToPagos={() => setActiveTab('pagos')}
              onGoToPlan={() => setActiveTab('plan')}
            />
          )}
          {activeTab === 'familia' && (
            <ScreenFamilia
              user={userForScreen}
              memberId={userForScreen.id}
              openAddSignal={openAddBeneficiarySignal}
            />
          )}
          {activeTab === 'pagos' && <ScreenPagos user={userForScreen} memberId={userForScreen.id} />}
          {activeTab === 'plan' && <ScreenPlan user={userForScreen} />}
        </div>
        <BottomNav activeTab={activeTab} onTabChange={setActiveTab} onCenterAction={handleAddBeneficiary} />
      </div>
    </ErrorBoundary>
  );
}
